import {Component, Injector, OnInit} from '@angular/core';
import {BaseComponent} from "../../base.component";
import {SnackBarUtil} from "../../../util/snack.bar.util";
import {StringUtil} from "../../../util/string.util";
import {Params} from "@angular/router";
import {DataResponse} from "../../../network/service/data.response";
import {FilterUtil} from "../../../util/filter.util";
import {RequestType} from '../../../enums/request.type';
import {ListResponse} from '../../../network/service/list.response';
import {FactorStatusHelper} from '../../../enums/factor.status';

@Component({
  selector: 'app-user-detail',
  standalone: false,
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss']
})
export class UserDetailComponent extends BaseComponent implements OnInit {
  displayedColumns = ['TaskInstanceId', 'ProcessTitle', 'TaskTitle', 'Status', 'CreateDate', 'Decision'];
  displayDataSources = ['TaskInstanceId', 'Task.Process.Title', 'TaskTitleAsString', 'StatusAsString', 'CreateDate', 'arrow_outward'];
  displayColumnTypes = ['string', 'string', 'string', 'string', 'date:HH:mm:ss YYYY/MM/DD', 'event'];
  displayEndPoint = ['', '', '', '', '', ''];
  reports?: any = [];
  logicalIncomeInformation: any;
  orders: any = [];
  user?: any;
  userAttributes?: any = [];
  userExtraAttributes?: any = [];
  userComments?: any = [];
  taskInstances?: any = [];
  allFilters = FilterUtil.FactorsFilters;
  filters: any = {};
  userId?: string;
  activeTab = 0;
  showDetailedAttributes = false;
  showDetailedOrders = false;
  selectedTemplate = '';
  smsMessage = '';
  protected readonly StringUtil = StringUtil;

  constructor(injector: Injector) {
    super(injector);
    this.activatedRoute.params.subscribe((params: Params) => {
      if (params['UserId'] && params['UserId'] !== '') {
        this.userId = params['UserId'];
      }
    });
  }

  onFiltersChanged(filters: any) {
    this.filters = filters;
    this.tableProperties.offset = 0;
    this.loadOnline();
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override loadOnline() {
    super.loadOnline();
    this.serviceApi.getUsers(undefined, undefined, 'UserAttributes,UserAttributes.Attribute,UserAttributes.Attribute.AttributeType,UserExtraAttributes,UserExtraAttributes.Attribute,UserExtraAttributes.Attribute.AttributeType', RequestType.ONE, {
      Filter: {
        Values: [{
          Field: 'UserId',
          Type: 'EQ',
          Value: this.userId
        }],
        Type: 'AND'
      }
    }).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.user = res.data;
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
    this.serviceApi.getFactors(this.tableProperties.offset, this.tableProperties.limit, 'User,ServicePrice,Order,FactorOrders,FactorOrders.Order,FactorOrders.Order.ServicePrice,FactorOrders.Order.ServicePrice.Service,FactorOrders.Order.OrderServicePriceAdditives,FactorOrders.Order.OrderServicePriceAdditives.ServicePriceAdditive,FactorOrders.Order.OrderServicePriceAdditives.ServicePriceAdditive.Attribute,DeliveryMethod,UserAddress,Space', undefined, {
      Filter: {
        Values: [{
          Field: 'UserId',
          Type: 'EQ',
          Value: this.userId
        }],
        Type: 'AND'
      },
      Order: this.tableProperties.order ?? {
        Field: 'ModifyDate',
        Type: 'DESC'
      }
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.orders = res.data;
        this.tableProperties.pageLength = res.count;
        this.setListeners();
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  override setListeners() {
    super.setListeners();
    for (let i = 0; i < this.taskInstances.length; i++) {
    }
  }

  override pageChanged(event: any) {
    super.pageChanged(event);
    this.tableProperties.offset = (event.pageIndex);
    this.loadOnline();
  }

  override onSortChanged(event: any) {
    super.onSortChanged(event);
    this.loadOnline();
  }

  getStatusClass(status: string): string {
    // Map status to CSS class based on FactorStatusHelper colors
    const color = FactorStatusHelper.getStatusColor(status);
    if (color === '#2196f3') return 'status-submit';
    if (color === '#ff9800') return 'status-waiting';
    if (color === '#9c27b0') return 'status-waiting';
    if (color === '#607d8b') return 'status-preparation';
    if (color === '#ff5722') return 'status-sent';
    if (color === '#795548') return 'status-sent';
    if (color === '#4caf50') return 'status-delivered';
    if (color === '#f44336') return 'status-canceled';
    return 'status-default';
  }

  getStatusIcon(status: string): string {
    return FactorStatusHelper.getStatusIcon(status);
  }

  getStatusLabel(status: string): string {
    const statusKey = FactorStatusHelper.getStatusText(status);
    return this.translateService.instant(statusKey);
  }

  toggleAttributes(): void {
    this.showDetailedAttributes = !this.showDetailedAttributes;
  }

  toggleOrders(): void {
    this.showDetailedOrders = !this.showDetailedOrders;
  }

  getRecentOrdersCount(): number {
    if (!this.orders || this.orders.length === 0) return 0;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return this.orders.filter((order: any) => {
      const orderDate = new Date(order.CreateDate);
      return orderDate >= thirtyDaysAgo;
    }).length;
  }

  scrollToOrders(): void {
    setTimeout(() => {
      const ordersSection = document.querySelector('.detailed-section:last-child');
      if (ordersSection) {
        ordersSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  }

  exportOrders(): void {
    if (!this.orders || this.orders.length === 0) {
      SnackBarUtil.makeMessage(this, this.translateService.instant('NoOrdersToExport'), false);
      return;
    }
    const csvContent = this.createOrdersCSV();
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_${this.user?.FirstName}_${this.user?.LastName}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    SnackBarUtil.makeMessage(this, this.translateService.instant('OrdersExportedSuccessfully'), true);
  }

  showAllComments(): void {
  }

  onTemplateChange(): void {
    switch (this.selectedTemplate) {
      case 'welcome':
        this.smsMessage = this.translateService.instant('WelcomeMessageTemplate').replace('{name}', this.user?.FirstName || '');
        break;
      case 'promotion':
        this.smsMessage = this.translateService.instant('PromotionMessageTemplate').replace('{name}', this.user?.FirstName || '');
        break;
      case 'reminder':
        this.smsMessage = this.translateService.instant('ReminderMessageTemplate').replace('{name}', this.user?.FirstName || '');
        break;
      case 'custom':
        this.smsMessage = '';
        break;
      default:
        this.smsMessage = '';
    }
  }

  sendSMS(): void {
    if (!this.smsMessage.trim()) {
      SnackBarUtil.makeMessage(this, this.translateService.instant('PleaseEnterAMessage'), false);
      return;
    }
    if (!this.user?.MobileNumber) {
      SnackBarUtil.makeMessage(this, this.translateService.instant('CustomerMobileNumberNotAvailable'), false);
      return;
    }
    SnackBarUtil.makeMessage(this, this.translateService.instant('SMSSentSuccessfully'), true);
    this.smsMessage = '';
    this.selectedTemplate = '';
  }

  previewSMS(): void {
    if (!this.smsMessage.trim()) {
      SnackBarUtil.makeMessage(this, this.translateService.instant('PleaseEnterAMessage'), false);
      return;
    }
    SnackBarUtil.makeMessage(this, this.translateService.instant('SMSPreviewShown'), true);
  }

  getUserRank(): string {
    if (!this.orders || this.orders.length === 0) return this.translateService.instant('NewMember');
    const totalAmount = this.getTotalPurchaseAmount();
    if (totalAmount >= 1000000) return this.translateService.instant('VIP');
    if (totalAmount >= 500000) return this.translateService.instant('Gold');
    if (totalAmount >= 200000) return this.translateService.instant('Silver');
    if (totalAmount >= 100000) return this.translateService.instant('Bronze');
    return this.translateService.instant('Regular');
  }

  getTotalPurchaseAmount(): number {
    if (!this.orders || this.orders.length === 0) return 0;
    return this.orders.reduce((total: number, order: any) => {
      const price = order.PayablePrice || 0;
      return total + Number(price);
    }, 0);
  }

  placeOrderForCustomer() {
    this.router.navigate(['/dashboard/PlaceOrder', this.userId]).then();
  }

  private createOrdersCSV(): string {
    const headers = [
      this.translateService.instant('OrderID'),
      this.translateService.instant('Date'),
      this.translateService.instant('Status'),
      this.translateService.instant('FullName'),
      this.translateService.instant('Phone'),
      this.translateService.instant('TotalAmount'),
      this.translateService.instant('DeliveryMethod'),
      this.translateService.instant('TableNumber')
    ];
    const rows = this.orders.map((order: any) => [
      order.FactorId,
      new Date(order.CreateDate).toLocaleDateString('fa-IR'),
      this.getStatusLabel(order.Status),
      `${order.User?.FirstName} ${order.User?.LastName}`,
      order.User?.MobileNumber,
      order.PayablePrice,
      order.DeliveryMethod?.Title || 'N/A',
      order.Space?.Title || 'N/A'
    ]);
    return [headers, ...rows]
      .map((row: any) => row.map((cell: any) => `"${cell}"`).join(','))
      .join('\n');
  }
}
